// dev-cart-fitment — "Guaranteed fit for <vehicle>" badge on cart line items.
// ───────────────────────────────────────────────────────────────────────────
// For each cart line that ships a compatible-vehicles matrix
// (snippets/cart-products.liquid renders product.metafields.fitment.fitment_data_v2
// into [data-cart-fit-data]), this checks the shopper's saved garage
// (localStorage['devGarage'], shared with the PDP / homepage pickers). If any saved
// vehicle is a GUARANTEED fit (matches a full Type→Year→Make→Model fitment row), it
// reveals the line's badge naming that vehicle. Mirrors the match logic in
// assets/dev-shop-my-ride.js (#rideMatch, guaranteed = full-depth match).
//
// Re-runs whenever the cart re-renders (quantity change, drawer open, ajax add) via
// a MutationObserver, and when the garage changes in another tab (storage event).

(function () {
  'use strict';
  if (window.__devCartFitmentInit) return;
  window.__devCartFitmentInit = true;

  const GARAGE_KEY = 'devGarage';
  const TAGS = ['type', 'year', 'make', 'model'];

  const ICONS = {
    bike: '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"><circle cx="11" cy="24" r="6"/><circle cx="37" cy="24" r="6"/><path d="M11 24l8-10h9l4 10M19 14l-3-4h-5"/></svg>',
    atv: '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"><circle cx="10" cy="23" r="7"/><circle cx="38" cy="23" r="7"/><path d="M6 16h12l4-5h10l6 5h4"/></svg>',
    snow: '<svg viewBox="0 0 28 18" fill="currentColor"><path d="M2 12.5c2.2 0 3.2-1.2 5.5-1.2 1.6 0 2.4.6 3.9.6 2 0 3-1.6 5.1-1.6 1.3 0 2 .5 3.3.5 1.6 0 2.4-.8 4.2-.8v2.2c-1.8 0-2.6.8-4.2.8-1.3 0-2-.5-3.3-.5-2.1 0-3.1 1.6-5.1 1.6-1.5 0-2.3-.6-3.9-.6-2.3 0-3.3 1.2-5.5 1.2zM18.5 5.2l4.2.9.5 2.1-6.6-1.4-7.4 1.6-7 .1L1.4 6l6.3-.1 1.7-2.9 4.6.4-1 1.9z"/></svg>',
  };
  function vehicleIcon(parts) {
    const t = String((parts && parts[0] && parts[0].value) || '').toLowerCase();
    if (t.includes('snow') || t.includes('sled')) return ICONS.snow;
    if (t.includes('atv') || t.includes('utv') || t.includes('quad') || t.includes('side')) return ICONS.atv;
    return ICONS.bike;
  }

  function readGarage() {
    try { const v = JSON.parse(localStorage.getItem(GARAGE_KEY)); return Array.isArray(v) ? v : []; }
    catch { return []; }
  }

  // ── fitment match (mirrors dev-shop-my-ride #rideMatch, "guaranteed" only) ──
  function strIndex(str, label) {
    const t = String(label).toLowerCase();
    for (let i = 0; i < str.length; i++) if (String(str[i]).toLowerCase() === t) return i;
    return -1;
  }
  function levelsOf(matrix) {
    return (matrix.options || [])
      .map((o, oi) => ({ oi, tag: o.tag }))
      .filter((o) => TAGS.includes(o.tag));
  }
  // True when some fitment row matches the vehicle on EVERY YMM level (full fit).
  function isGuaranteed(matrix, parts) {
    const str = matrix.str || [];
    const rows = Array.isArray(matrix.fitment) ? matrix.fitment : Object.values(matrix.fitment || {});
    const levels = levelsOf(matrix);
    if (!levels.length) return false;
    const idxByTag = {};
    for (const L of levels) {
      const p = (parts || []).find((pp) => (pp.tag || pp.key) === L.tag);
      idxByTag[L.tag] = p ? strIndex(str, p.value) : -1;
      if (idxByTag[L.tag] < 0) return false; // vehicle missing a level this product indexes
    }
    for (const e of rows) {
      let ok = true;
      for (const L of levels) {
        if (![e[L.oi]].flat(2).includes(idxByTag[L.tag])) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  function reveal(badge, veh) {
    const name = (veh.parts || []).map((p) => p.value).join(' ') || veh.name || '';
    const nameEl = badge.querySelector('[data-cart-fit-name]');
    const iconEl = badge.querySelector('[data-cart-fit-icon]');
    if (nameEl) nameEl.textContent = name;
    if (iconEl) iconEl.innerHTML = vehicleIcon(veh.parts);
    badge.hidden = false;
  }

  function applyOne(badge, garage) {
    // Show only when a saved vehicle is a guaranteed fit for this product.
    const row = badge.closest('.cart-items__table-row') || badge.parentElement;
    const dataEl = row && row.querySelector('[data-cart-fit-data]');
    let matrix = null;
    if (dataEl) { try { matrix = JSON.parse(dataEl.textContent); } catch (_) { matrix = null; } }
    if (!matrix) { badge.hidden = true; return; }

    let hit = null;
    for (const veh of (garage || [])) {
      const parts = Array.isArray(veh.parts) ? veh.parts : [];
      if (parts.length && isGuaranteed(matrix, parts)) { hit = veh; break; }
    }
    if (!hit) { badge.hidden = true; return; }
    reveal(badge, hit);
  }

  let observer = null;
  let scheduled = false;
  function applyAll() {
    scheduled = false;
    const badges = document.querySelectorAll('[data-cart-fit]');
    if (!badges.length) return;
    const garage = readGarage();
    if (observer) observer.disconnect(); // our own DOM writes must not retrigger us
    badges.forEach((b) => applyOne(b, garage));
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }
  function schedule() { if (!scheduled) { scheduled = true; requestAnimationFrame(applyAll); } }

  function init() {
    applyAll();
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('storage', (e) => { if (e.key === GARAGE_KEY) schedule(); }); // cross-tab
    document.addEventListener('dev-garage:change', schedule); // same-tab garage edits
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
